import React, { useState, useEffect, useCallback } from 'react';
import { reclamosApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Paperclip, X, MessageSquare, Download, FileText, Inbox } from 'lucide-react';

const ESTADO_STYLE = {
  nuevo: 'bg-blue-50 text-blue-700 border-blue-200',
  en_revision: 'bg-amber-50 text-amber-700 border-amber-200',
  respondido: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cerrado: 'bg-slate-100 text-slate-500 border-slate-200',
};

const EstadoBadge = ({ estado, label }) => (
  <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${ESTADO_STYLE[estado] || ESTADO_STYLE.nuevo}`}>
    {label || estado}
  </span>
);

const fmtFecha = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const UNIDAD_GENERAL = '__general__';

export default function PropietarioReclamos() {
  const [reclamos, setReclamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opciones, setOpciones] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reclamosApi.list();
      setReclamos(r.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los reclamos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchList();
    reclamosApi.opciones().then((r) => setOpciones(r.data)).catch(() => {});
    reclamosApi.misUnidades().then((r) => setUnidades(r.data || [])).catch(() => {});
  }, [fetchList]);

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-0 py-2 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Reclamos y Sugerencias</h1>
          <p className="text-sm text-slate-500">Creá un reclamo, consulta o sugerencia y seguí su estado.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="nuevo-reclamo-btn" className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : reclamos.length === 0 ? (
        <div className="py-16 flex flex-col items-center text-slate-400" data-testid="reclamos-empty">
          <Inbox className="w-10 h-10 mb-2" />
          <p className="text-sm">Todavía no tenés reclamos, consultas ni sugerencias.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reclamos-list">
          {reclamos.map((r) => (
            <button
              key={r.reclamo_id}
              onClick={() => setDetalleId(r.reclamo_id)}
              data-testid={`reclamo-item-${r.reclamo_id}`}
              className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-medium text-indigo-600 uppercase">{r.tipo_label}</span>
                    <span className="text-[11px] text-slate-400">· {r.tema_label}</span>
                  </div>
                  <div className="font-medium text-slate-900 truncate">{r.asunto}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.unidad_nombre} · {fmtFecha(r.created_at)}</div>
                </div>
                <EstadoBadge estado={r.estado} label={r.estado_label} />
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && opciones && (
        <CrearReclamoDialog
          opciones={opciones}
          unidades={unidades}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchList(); }}
        />
      )}

      {detalleId && (
        <DetalleReclamoDialog
          reclamoId={detalleId}
          onClose={() => setDetalleId(null)}
          onChanged={fetchList}
        />
      )}
    </div>
  );
}

// ─────────────── Adjuntos uploader ───────────────
const Adjuntos = ({ adjuntos, setAdjuntos, testidPrefix }) => {
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await reclamosApi.upload(fd);
      setAdjuntos((prev) => [...prev, r.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir el archivo');
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-1.5">
      <label className="inline-flex items-center gap-1.5 text-sm text-indigo-600 cursor-pointer hover:underline">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        Adjuntar (imagen o PDF)
        <input type="file" accept="image/*,.pdf" onChange={onPick} className="hidden" disabled={uploading} data-testid={`${testidPrefix}-file-input`} />
      </label>
      {adjuntos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {adjuntos.map((a) => (
            <span key={a.file_id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-2 py-1">
              <FileText className="w-3 h-3 text-slate-500" />
              <span className="max-w-[140px] truncate">{a.original_filename}</span>
              <button type="button" onClick={() => setAdjuntos((p) => p.filter((x) => x.file_id !== a.file_id))}>
                <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────── Crear ───────────────
const CrearReclamoDialog = ({ opciones, unidades, onClose, onCreated }) => {
  const [tipo, setTipo] = useState('');
  const [tema, setTema] = useState('');
  const [motivo, setMotivo] = useState('');
  const [unidad, setUnidad] = useState(UNIDAD_GENERAL);
  const [asunto, setAsunto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [saving, setSaving] = useState(false);

  const motivos = tema ? (opciones.motivos[tema] || []) : [];

  const submit = async () => {
    if (!tipo || !tema || !motivo) return toast.error('Completá tipo, tema y motivo');
    if (!asunto.trim()) return toast.error('Ingresá un asunto');
    if (!descripcion.trim()) return toast.error('Ingresá una descripción');
    setSaving(true);
    try {
      await reclamosApi.crear({
        tipo, tema, motivo,
        unidad_id: unidad === UNIDAD_GENERAL ? null : unidad,
        asunto: asunto.trim(),
        descripcion: descripcion.trim(),
        adjuntos: adjuntos.map((a) => a.file_id),
      });
      toast.success('Tu solicitud fue enviada');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la solicitud');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crear-reclamo-dialog">
        <DialogHeader><DialogTitle>Nueva solicitud</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Tipo de solicitud</label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="reclamo-tipo"><SelectValue placeholder="Seleccioná..." /></SelectTrigger>
              <SelectContent>{opciones.tipos.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Tema</label>
            <Select value={tema} onValueChange={(v) => { setTema(v); setMotivo(''); }}>
              <SelectTrigger data-testid="reclamo-tema"><SelectValue placeholder="Seleccioná..." /></SelectTrigger>
              <SelectContent>{opciones.temas.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Motivo</label>
            <Select value={motivo} onValueChange={setMotivo} disabled={!tema}>
              <SelectTrigger data-testid="reclamo-motivo"><SelectValue placeholder={tema ? 'Seleccioná...' : 'Elegí un tema primero'} /></SelectTrigger>
              <SelectContent>{motivos.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Unidad relacionada</label>
            <Select value={unidad} onValueChange={setUnidad}>
              <SelectTrigger data-testid="reclamo-unidad"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNIDAD_GENERAL}>General / No corresponde a una unidad específica</SelectItem>
                {unidades.map((u) => <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.nombre_unidad}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Asunto</label>
            <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Resumen breve" data-testid="reclamo-asunto" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Descripción</label>
            <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4} placeholder="Contanos en detalle..." data-testid="reclamo-descripcion" />
          </div>
          <Adjuntos adjuntos={adjuntos} setAdjuntos={setAdjuntos} testidPrefix="crear" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="reclamo-submit">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────── Detalle ───────────────
const AdjuntoLink = ({ a }) => {
  const [busy, setBusy] = useState(false);
  const abrir = async () => {
    setBusy(true);
    try {
      const r = await reclamosApi.download(a.file_id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('No se pudo abrir el adjunto'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={abrir} disabled={busy} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline" data-testid={`adjunto-${a.file_id}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      <span className="max-w-[160px] truncate">{a.original_filename}</span>
    </button>
  );
};

const DetalleReclamoDialog = ({ reclamoId, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comentario, setComentario] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [sending, setSending] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reclamosApi.detalle(reclamoId);
      setDoc(r.data);
    } catch { toast.error('No se pudo cargar el detalle'); onClose(); }
    finally { setLoading(false); }
  }, [reclamoId, onClose]);

  useEffect(() => { fetch(); }, [fetch]);

  const enviarComentario = async () => {
    if (!comentario.trim()) return;
    setSending(true);
    try {
      await reclamosApi.comentar(reclamoId, { texto: comentario.trim(), adjuntos: adjuntos.map((a) => a.file_id) });
      setComentario(''); setAdjuntos([]);
      await fetch();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar el comentario');
    } finally { setSending(false); }
  };

  const cerrado = doc?.estado === 'cerrado';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="detalle-reclamo-dialog">
        {loading || !doc ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-medium text-indigo-600 uppercase">{doc.tipo_label}</span>
                <EstadoBadge estado={doc.estado} label={doc.estado_label} />
              </div>
              <DialogTitle className="text-base">{doc.asunto}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div><span className="text-slate-400">Tema:</span> {doc.tema_label}</div>
                <div><span className="text-slate-400">Motivo:</span> {doc.motivo}</div>
                <div className="col-span-2"><span className="text-slate-400">Unidad:</span> {doc.unidad_nombre}</div>
                <div className="col-span-2"><span className="text-slate-400">Creado:</span> {fmtFecha(doc.created_at)}</div>
              </div>
              <div className="bg-slate-50 rounded-md p-3 whitespace-pre-wrap text-slate-800">{doc.descripcion}</div>
              {doc.adjuntos?.length > 0 && (
                <div className="flex flex-wrap gap-2">{doc.adjuntos.map((a) => <AdjuntoLink key={a.file_id} a={a} />)}</div>
              )}

              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Historial ({doc.comentarios?.length || 0})
                </div>
                {(!doc.comentarios || doc.comentarios.length === 0) && (
                  <p className="text-xs text-slate-400">Sin comentarios aún.</p>
                )}
                <div className="space-y-2">
                  {(doc.comentarios || []).map((c) => (
                    <div key={c.comentario_id} className={`rounded-md p-2.5 ${c.autor_tipo === 'propietario' ? 'bg-indigo-50' : 'bg-emerald-50'}`}>
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                        <span className="font-medium">{c.autor_nombre}{c.autor_tipo !== 'propietario' ? ' · Equipo' : ''}</span>
                        <span>{fmtFecha(c.created_at)}</span>
                      </div>
                      <div className="text-slate-800 whitespace-pre-wrap">{c.texto}</div>
                      {c.adjuntos?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1.5">{c.adjuntos.map((a) => <AdjuntoLink key={a.file_id} a={a} />)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {cerrado ? (
                <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">Este caso está cerrado.</p>
              ) : (
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3} placeholder="Agregar un comentario..." data-testid="comentario-texto" />
                  <Adjuntos adjuntos={adjuntos} setAdjuntos={setAdjuntos} testidPrefix="comentario" />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={enviarComentario} disabled={sending || !comentario.trim()} data-testid="comentario-submit">
                      {sending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Comentar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
